import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { getMetaBrowserIds } from './metaPixel';
import type { PlanId } from '@shared/types';

// Mobile-only. Mints the Stripe HOSTED Checkout Session ahead of the tap, so the
// button press is a redirect and nothing else.
//
// Why this is not the same mistake as prefetchCheckoutSubscription:
// that one calls createGuestSubscription, which does stripe.subscriptions.create()
// — a real Customer plus an incomplete Subscription per prefetch, which is what
// left 100+ incomplete_expired subscriptions in the live account. A hosted
// Checkout Session creates NO Customer and NO PaymentIntent until the buyer
// actually submits the form; an unused one just expires. So pre-creating one is
// cheap in a way pre-creating a subscription never was.
//
// The tap itself does zero work beyond reading a string. Everything the old flow
// did between the tap and Stripe — one callable roundtrip plus one Stripe API
// call, roughly a second on mobile data — now happens while the user is still
// reading the plan cards.

interface Entry {
  promise: Promise<string>;
  ts: number;
}

// Stripe expires a Checkout Session after 24h. The cap here is about the user,
// not Stripe: past a few minutes on the paywall the price on screen may have been
// re-rendered from a newer bundle, so re-mint rather than redirect to a stale one.
const TTL_MS = 10 * 60_000;

const cache = new Map<PlanId, Entry>();

function fresh(planId: PlanId): Entry | null {
  const hit = cache.get(planId);
  if (!hit) return null;
  if (Date.now() - hit.ts > TTL_MS) {
    cache.delete(planId);
    return null;
  }
  return hit;
}

function mint(planId: PlanId): Promise<string> {
  const referral = (window as unknown as { Rewardful?: { referral?: string } }).Rewardful?.referral;
  const createHosted = httpsCallable<
    { planId: PlanId; referral?: string; metaFbp?: string; metaFbc?: string },
    // `sessionUrl`, not `url`. Reading the wrong key here fails silently into
    // "No checkout URL returned" and kills every mobile checkout.
    { sessionUrl: string }
  >(functions, 'createGuestCheckout');

  return createHosted({ planId, ...(referral ? { referral } : {}), ...getMetaBrowserIds() }).then(({ data }) => {
    if (!data?.sessionUrl) throw new Error('No checkout URL returned');
    return data.sessionUrl;
  });
}

/**
 * Fire-and-forget. Safe to call repeatedly — a live entry is reused, and a
 * failure drops itself from the cache so the tap can retry inline.
 */
export function prefetchHostedCheckout(planId: PlanId): void {
  if (fresh(planId)) return;
  const promise = mint(planId).catch((err) => {
    cache.delete(planId);
    throw err;
  });
  // Swallow here as well: an unconsumed rejected promise is an unhandled
  // rejection, and this one is genuinely optional work.
  promise.catch(() => {});
  cache.set(planId, { promise, ts: Date.now() });
}

/**
 * The session URL for this plan: instant if prefetched, otherwise a fresh mint.
 * Never throws synchronously — the caller awaits one promise either way.
 */
export function getHostedCheckoutUrl(planId: PlanId): Promise<string> {
  const hit = fresh(planId);
  if (hit) return hit.promise;
  const promise = mint(planId);
  cache.set(planId, { promise, ts: Date.now() });
  promise.catch(() => cache.delete(planId));
  return promise;
}
